import { ColumnDef } from "@tanstack/react-table";
import { Button, Spinner, TextInput } from "flowbite-react";
import { Copy, Megaphone, TrendDown, TrendUp } from "@phosphor-icons/react";
import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import AnimatedModal from "../../../../components/AnimatedModal";
import CountryDisplay from "../../../../components/CountryDisplay";
import SearchInput from "../../../../components/SearchInput";
import { Table } from "../../../../components/Table";
import { useIsMobile } from "../../../../hooks";
import { useCampaigns } from "../../../../hooks/get/useCampaigns";
import { useCampaignCreate } from "../../../../hooks/post/useCampaignCreate";
import { ISelectOption } from "../../../../interfaces";
import {
  BIDDING_STRATEGY_OPTIONS,
  customSelectStyles,
  DEFAULT_ITEMS_PER_PAGE,
} from "../../../../shared/constants";
import COUNTRY_OPTIONS from "../../../../shared/country.json";
import { ICampaignData, IDuplicateCampaignForm } from "../../interface";

const animatedComponents = makeAnimated();

const initialCampaignData: IDuplicateCampaignForm = {
  campaign_id: 0,
  campaign_name: "",
  campaign_app_package_id: "",
  campaign_end_date: "",
  campaign_budget: 0,
  campaign_country: [],
  campaign_bidding_strategy: BIDDING_STRATEGY_OPTIONS[0],
  campaign_target_cpa: 0,
  campaign_target_roas: 0,
};

const CampaignList: React.FC = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [searchTerm, setSearchTerm] = useState("");
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [filteredCampaigns, setFilteredCampaigns] = useState<ICampaignData[]>(
    []
  );
  const [duplicateForm, setDuplicateForm] =
    useState<IDuplicateCampaignForm>(initialCampaignData);

  const { data: campaignsData, isLoading } = useCampaigns();
  const { mutateAsync: duplicateCampaign, isPending: isDuplicating } =
    useCampaignCreate();

  const campaigns = useMemo(
    () => campaignsData?.results || [],
    [campaignsData]
  );

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };
  useEffect(() => {
    let filtered = [...campaigns];

    if (searchTerm) {
      filtered = filtered.filter((campaign) => {
        const searchLower = searchTerm.toLowerCase();
        // Convert campaign object to searchable string
        const searchableString = Object.entries(campaign)
          .filter(([, value]) => value !== null && value !== undefined)
          .map(([key, value]) => {
            if (Array.isArray(value)) {
              return value.join(" ");
            }
            if (typeof value === "object") {
              return JSON.stringify(value);
            }
            // Format dates as DD/MM/YYYY
            if (key.includes("date")) {
              const date = new Date(value);
              if (!isNaN(date.getTime())) {
                return date.toLocaleDateString("en-GB"); // Returns DD/MM/YYYY
              }
            }
            return String(value);
          })
          .join(" ")
          .toLowerCase();

        return searchableString.includes(searchLower);
      });
    }

    setFilteredCampaigns(filtered);
  }, [searchTerm, campaigns]);

  const handleDuplicate = (campaign: ICampaignData) => {
    setDuplicateForm({
      campaign_id: campaign.google_campaign_id,
      campaign_name: `Copy - ${campaign.google_campaign_name}`,
      campaign_app_package_id: campaign.google_app_package_id,
      campaign_end_date: campaign.google_campaign_end_date,
      campaign_budget: "",
      campaign_country: campaign.google_campaign_multiple_country_code.map(
        (ct) => COUNTRY_OPTIONS.find((c) => c.value === ct) as ISelectOption
      ),
      campaign_bidding_strategy:
        campaign.google_campaign_bidding_strategy_type === "TARGET_CPA"
          ? BIDDING_STRATEGY_OPTIONS[0]
          : BIDDING_STRATEGY_OPTIONS[1],
      campaign_target_cpa:
        campaign.google_campaign_bidding_strategy_type === "TARGET_CPA"
          ? (campaign.google_campaign_bid / 1000000).toString()
          : "0",
      campaign_target_roas:
        campaign.google_campaign_bidding_strategy_type === "TARGET_ROAS"
          ? campaign.google_campaign_troas.toString()
          : "0",
    });
    setShowDuplicateModal(true);
  };

  const handleDuplicateSubmit = async () => {
    if (duplicateForm.campaign_budget === "") {
      toast.error("Campaign budget is required");
      return;
    }

    if (duplicateForm.campaign_target_cpa === "") {
      toast.error("Target CPA is required");
      return;
    }

    if (duplicateForm.campaign_target_roas === "") {
      toast.error("Target ROAS is required");
      return;
    }

    // Validate numeric values
    if (
      isNaN(Number(duplicateForm.campaign_budget)) ||
      Number(duplicateForm.campaign_budget) <= 0
    ) {
      toast.error("Campaign budget must be a positive number");
      return;
    }

    if (
      duplicateForm.campaign_bidding_strategy.value ===
      BIDDING_STRATEGY_OPTIONS[0].value &&
      (isNaN(Number(duplicateForm.campaign_target_cpa)) ||
        Number(duplicateForm.campaign_target_cpa) <= 0)
    ) {
      toast.error("Target CPA must be a positive number");
      return;
    }

    if (
      duplicateForm.campaign_bidding_strategy.value ===
      BIDDING_STRATEGY_OPTIONS[1].value &&
      (isNaN(Number(duplicateForm.campaign_target_roas)) ||
        Number(duplicateForm.campaign_target_roas) <= 0)
    ) {
      toast.error("Target ROAS must be a positive number");
      return;
    }

    try {
      const { campaign_id, ...restDuplicateForm } = duplicateForm;
      const payload = {
        campaign_name: restDuplicateForm.campaign_name,
        campaign_app_package_id: restDuplicateForm.campaign_app_package_id,
        campaign_end_date: restDuplicateForm.campaign_end_date,
        campaign_country: restDuplicateForm.campaign_country.map(
          (country) => country.value
        ),
        campaign_bidding_strategy:
          restDuplicateForm.campaign_bidding_strategy.value,
        campaign_target_cpa: Number(restDuplicateForm.campaign_target_cpa),
        campaign_target_roas: Number(restDuplicateForm.campaign_target_roas),
        campaign_budget: Number(restDuplicateForm.campaign_budget),
      };

      await duplicateCampaign({
        campaign_id: Number(campaign_id),
        payload,
      });
      toast.success("Campaign duplicated successfully");
      setShowDuplicateModal(false);
      setDuplicateForm(initialCampaignData);
    } catch (error) {
      console.error("Error duplicating campaign:", error);
      toast.error("Failed to duplicate campaign");
    }
  };

  // Define columns for the table
  const columns: ColumnDef<ICampaignData>[] = [
    {
      accessorKey: "google_campaign_name",
      header: t("campaigns.columns.name"),
      size: 300,
      cell: ({ row }) => {
        const campaign = row.original;
        return (
          <div className="flex items-center gap-2">
            <div className="min-w-10 w-10 min-h-10 h-10 flex justify-center items-center border border-gray-200 dark:border-gray-700 rounded-md">
              <Megaphone className="w-6 h-6" />
            </div>
            <div className="flex flex-col">
              <p>{campaign.google_campaign_name}</p>
              <span className="text-xs text-gray-500">
                {campaign.google_campaign_advertising_channel_type}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "google_campaign_multiple_country_code",
      header: t("campaigns.columns.country"),
      cell: ({ row }) => {
        const campaign = row.original;
        const countryCode = campaign.google_campaign_multiple_country_code[0];
        // Get country name from the country options
        const countryOption = COUNTRY_OPTIONS.find(
          (option) => option.value === countryCode
        );
        const countryName = countryOption ? countryOption.label : countryCode;

        return (
          <CountryDisplay
            isUniversal={!countryCode || campaign.is_universal_campaign}
            countryCode={countryCode}
            countryName={countryName}
          />
        );
      },
    },
    {
      accessorKey: "google_app_package_id",
      header: t("campaigns.columns.appId"),
      cell: ({ row }) => (
        <div className="flex flex-col">
          <span>{row.original.google_app_package_id}</span>
          <span className="text-xs text-gray-500">
            {row.original.google_app_store}
          </span>
        </div>
      ),
    },
    {
      accessorKey: "google_campaign_start_date",
      header: t("campaigns.columns.startDate"),
      cell: ({ row }) =>
        new Date(row.original.google_campaign_start_date).toLocaleDateString(),
    },
    {
      accessorKey: "google_campaign_end_date",
      header: t("campaigns.columns.endDate"),
      cell: ({ row }) =>
        new Date(row.original.google_campaign_end_date).toLocaleDateString(),
    },
    {
      accessorKey: "google_campaign_bidding_strategy_type",
      header: t("campaigns.columns.biddingStrategy"),
      cell: ({ row }) => {
        const campaign = row.original;
        const isCPA =
          campaign.google_campaign_bidding_strategy_type === "TARGET_CPA";

        // Get current and previous values for comparison
        const currentValue = isCPA
          ? campaign.google_campaign_bid
          : campaign.google_campaign_troas;
        const previousValue = isCPA
          ? campaign.google_campaign_previous_bid
          : campaign.google_campaign_previous_troas;

        // Format the bid values
        const bid = isCPA
          ? `${(campaign.google_campaign_bid / 1000000).toFixed(2)}`
          : `${campaign.google_campaign_troas.toFixed(2)}%`;
        const previousBid = isCPA
          ? `${(campaign.google_campaign_previous_bid / 1000000).toFixed(2)}`
          : `${campaign.google_campaign_previous_troas.toFixed(2)}%`;

        // Determine colors based on value comparison
        const currentColor =
          currentValue > previousValue
            ? "text-green-600 font-medium"
            : currentValue < previousValue
              ? "text-red-600 font-medium"
              : "text-gray-900";

        return (
          <div className="flex flex-col">
            <span>{campaign.google_campaign_bidding_strategy_type}</span>
            <div className="flex items-center gap-1 text-sm">
              <span className="text-gray-500">{previousBid}</span>
              <span className={currentColor}>
                {currentValue > previousValue ? (
                  <TrendUp className="text-green-500" size={20} />
                ) : (
                  <TrendDown className="text-red-500" size={20} />
                )}
              </span>
              <span className={currentColor}>{bid}</span>
            </div>
          </div>
        );
      },
    },
    // {
    //   accessorKey: "google_campaign_status",
    //   header: t("campaigns.columns.status"),
    //   cell: ({ row }) => {
    //     const campaign = row.original;
    //     const status = campaign.is_paused_on
    //       ? "PAUSED"
    //       : campaign.google_campaign_status === "2"
    //       ? "ENABLED"
    //       : "REMOVED";
    //     const statusColor =
    //       status === "ENABLED"
    //         ? "text-green-500"
    //         : status === "PAUSED"
    //         ? "text-yellow-500"
    //         : "text-red-500";

    //     return <span className={`${statusColor} font-medium`}>{status}</span>;
    //   },
    // },
    // {
    //   id: "actions",
    //   header: t("campaigns.columns.actions"),
    //   size: 60,
    //   cell: ({ row }) => (
    //     <Button
    //       color="light"
    //       size="xs"
    //       title={t("campaigns.duplicate.title")}
    //       onClick={() => handleDuplicate(row.original)}
    //     >
    //       <Copy size={16} />
    //     </Button>
    //   ),
    // },
  ];

  return (
    <div>
      {/* Mobile Layout */}
      <div className="flex flex-col gap-4 sm:hidden">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Megaphone size={20} className="text-gray-700" />
            <h1 className="text-xl font-bold text-gray-700">
              {t("campaigns.title")}
            </h1>
          </div>
          <div className="flex gap-2">
            <SearchInput
              placeholder={t("campaigns.search")}
              onSearch={handleSearch}
            />
          </div>
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="hidden sm:flex sm:flex-row justify-between items-center">
        <div className="flex items-center gap-4">
          <Megaphone size={20} className="text-gray-700" />
          <h1 className="text-2xl font-bold text-gray-700">
            {t("campaigns.title")}
          </h1>
        </div>
        <div className="flex gap-2">
          <SearchInput
            placeholder={t("campaigns.search")}
            onSearch={handleSearch}
          />
        </div>
      </div>

      <div className="mt-4">
        <Table
          data={filteredCampaigns}
          columns={columns}
          isLoading={isLoading}
          showPagination={true}
          loadingHeight={4}
          loadingRowCount={10}
          pageSize={DEFAULT_ITEMS_PER_PAGE}
          maxHeight="calc(100vh - 255px)"
          isMobile={isMobile}
        />
      </div>

      <AnimatedModal
        show={showDuplicateModal}
        onClose={() => setShowDuplicateModal(false)}
        title={t("campaigns.duplicate.title")}
        size="lg"
        footer={
          <div className="flex justify-end gap-2 w-full">
            <Button
              color="light"
              disabled={isDuplicating}
              onClick={() => {
                setShowDuplicateModal(false);
                setDuplicateForm(initialCampaignData);
              }}
            >
              {t("common.cancel")}
            </Button>
            <Button
              isProcessing={isDuplicating}
              processingSpinner={<Spinner color="purple" size="sm" />}
              color="primary"
              onClick={handleDuplicateSubmit}
            >
              {t("campaigns.duplicate.submit", "Duplicate")}
            </Button>
          </div>
        }
      >
        <div className="space-y-6 max-h-[calc(100vh-260px)] overflow-y-auto px-1">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("campaigns.form.name")} *
            </label>
            <TextInput
              value={duplicateForm.campaign_name}
              onChange={(e) =>
                setDuplicateForm({
                  ...duplicateForm,
                  campaign_name: e.target.value,
                })
              }
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("campaigns.form.appPackageId")} *
            </label>
            <TextInput
              value={duplicateForm.campaign_app_package_id}
              onChange={(e) =>
                setDuplicateForm({
                  ...duplicateForm,
                  campaign_app_package_id: e.target.value,
                })
              }
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("campaigns.form.endDate")} *
            </label>
            <TextInput
              type="date"
              value={duplicateForm.campaign_end_date}
              onChange={(e) =>
                setDuplicateForm({
                  ...duplicateForm,
                  campaign_end_date: e.target.value,
                })
              }
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("campaigns.form.budget")} *
            </label>
            <TextInput
              type="number"
              value={duplicateForm.campaign_budget}
              onChange={(e) =>
                setDuplicateForm({
                  ...duplicateForm,
                  campaign_budget: e.target.value,
                })
              }
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("campaigns.form.country")}
            </label>
            <Select
              isMulti
              classNamePrefix={"custom-select"}
              options={COUNTRY_OPTIONS}
              value={duplicateForm.campaign_country}
              components={animatedComponents}
              onChange={(selectedOptions) =>
                setDuplicateForm({
                  ...duplicateForm,
                  campaign_country: selectedOptions as {
                    label: string;
                    value: string;
                  }[],
                })
              }
              styles={customSelectStyles<ISelectOption>()}
              placeholder={t("campaigns.duplicate.form.selectCountry")}
            />
            <p className="text-sm text-gray-500 mt-1">
              {t("campaigns.form.countryHelp")}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("campaigns.form.biddingStrategy")} *
            </label>
            <Select
              classNamePrefix={"custom-select"}
              options={BIDDING_STRATEGY_OPTIONS}
              value={duplicateForm.campaign_bidding_strategy}
              onChange={(selectedOption) =>
                setDuplicateForm({
                  ...duplicateForm,
                  campaign_bidding_strategy: selectedOption as {
                    label: string;
                    value: string;
                  },
                })
              }
              styles={customSelectStyles<ISelectOption>()}
            />
          </div>
          {duplicateForm.campaign_bidding_strategy.value ===
            BIDDING_STRATEGY_OPTIONS[0].value && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("campaigns.form.targetCpa")} *
                </label>
                <TextInput
                  type="number"
                  value={duplicateForm.campaign_target_cpa}
                  onChange={(e) =>
                    setDuplicateForm({
                      ...duplicateForm,
                      campaign_target_cpa: e.target.value,
                    })
                  }
                />
              </div>
            )}
          {duplicateForm.campaign_bidding_strategy.value ===
            BIDDING_STRATEGY_OPTIONS[1].value && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("campaigns.form.targetRoas")} *
                </label>
                <TextInput
                  type="number"
                  value={duplicateForm.campaign_target_roas}
                  onChange={(e) =>
                    setDuplicateForm({
                      ...duplicateForm,
                      campaign_target_roas: e.target.value,
                    })
                  }
                />
              </div>
            )}
        </div>
      </AnimatedModal>
    </div>
  );
};

export default CampaignList;
